/**
 * Main Navigation Component
 * Provides top-level navigation for Chat, Search, and KPIs
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Search, BarChart3 } from 'lucide-react';

export interface MainNavigationProps {
  className?: string;
}

export const MainNavigation: React.FC<MainNavigationProps> = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/chat')) return 'chat';
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/kpi')) return 'kpis';
    return 'chat'; // Default to chat
  };

  const activeTab = getActiveTab();

  const navigationItems = [
    {
      id: 'chat',
      label: 'Chat',
      icon: MessageSquare,
      path: '/',
      description: 'AI-Chat für Fragen und Unterstützung'
    },
    {
      id: 'search',
      label: 'Suche',
      icon: Search,
      path: '/search',
      description: 'Wissensdatenbank durchsuchen'
    },
    {
      id: 'kpis',
      label: 'KPIs',
      icon: BarChart3,
      path: '/kpi',
      description: 'Leistungskennzahlen und Berichte'
    }
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <nav className={`flex items-center space-x-1 ${className}`}>
      {navigationItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;
        
        return (
          <button
            key={item.id}
            onClick={() => handleNavigation(item.path)}
            className={`px-3 py-1.5 rounded-lg transition-smooth flex items-center space-x-2 ${
              isActive 
                ? 'bg-primary-600 text-white' 
                : 'hover:bg-white/10 text-gray-300 hover:text-white'
            }`}
            title={item.description}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};